import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  Stack,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  Avatar,
  LinearProgress,
  Fade,
  Zoom,
  // Tooltip,
  alpha,
  useTheme
  // useMediaQuery,
} from '@mui/material';
import {
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayArrowIcon,
  SupportAgent as SupportAgentIcon,
  AttachMoney as AttachMoneyIcon,
  Shield as ShieldIcon,
  SmartToy as SmartToyIcon,
  Assessment as AssessmentIcon,
  Transform as TransformIcon,
  Restore as RestoreIcon,
  Visibility as VisibilityIcon,
  CheckBox as CheckBoxIcon,
  HelpOutline as HelpOutlineIcon,
  DataObject as DataObjectIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  // RemoveRedEye as RemoveRedEyeIcon,
  // Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  // TrendingUp as TrendingUpIcon,
  // CompareArrows as CompareArrowsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';

// Simple markdown-like formatter
const formatMarkdownToHTML = (text: string): string => {
  if (!text) return '';

  let html = text;

  // Convert headers
  html = html.replace(
    /^### (.+)$/gm,
    '<h3 style="color: #667eea; margin-top: 20px; margin-bottom: 10px; font-size: 1.2em; font-weight: 600;">$1</h3>'
  );
  html = html.replace(
    /^## (.+)$/gm,
    '<h3 style="color: #667eea; margin-top: 20px; margin-bottom: 10px; font-size: 1.2em; font-weight: 600;">$1</h3>'
  );
  html = html.replace(
    /^# (.+)$/gm,
    '<h3 style="color: #667eea; margin-top: 20px; margin-bottom: 10px; font-size: 1.2em; font-weight: 600;">$1</h3>'
  );

  // Convert bold
  html = html.replace(
    /\*\*(.+?)\*\*/g,
    '<strong style="color: #333; font-weight: 600;">$1</strong>'
  );
  html = html.replace(/__(.+?)__/g, '<strong style="color: #333; font-weight: 600;">$1</strong>');

  // Convert italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Convert bullet lists
  html = html.replace(/^\*   (.+)$/gm, '<li style="margin: 8px 0; line-height: 1.6;">$1</li>');
  html = html.replace(/^\* (.+)$/gm, '<li style="margin: 8px 0; line-height: 1.6;">$1</li>');
  html = html.replace(/^- (.+)$/gm, '<li style="margin: 8px 0; line-height: 1.6;">$1</li>');

  // Wrap consecutive list items in ul
  html = html.replace(/(<li.*?>.*?<\/li>\n?)+/g, (match) => {
    return '<ul style="margin: 10px 0; padding-left: 30px;">' + match + '</ul>';
  });

  // Convert line breaks to paragraphs
  const paragraphs = html
    .split('\n\n')
    .map((para) => {
      para = para.trim();
      if (!para) return '';
      if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<li')) {
        return para;
      }
      return '<p style="margin: 10px 0;">' + para.replace(/\n/g, '<br>') + '</p>';
    })
    .join('\n');

  return paragraphs;
};

interface PIIDemoProps {
  apiBaseUrl?: string;
}

interface TicketDemo {
  processing_summary: {
    tickets_processed: number;
    pii_items_cleansed: number;
    processing_time_ms: number;
    status: string;
  };
  cleansing_details: Array<{
    ticket_id: string;
    pii_removed: number;
    pii_types: string[];
  }>;
  analysis: {
    llm_response: string;
    tokens_used: number;
  };
  privacy_guarantee: string;
  note: string;
}

interface FinancialDemo {
  results: Array<{
    application_id: string;
    pii_protection: {
      obfuscation_applied: boolean;
      restoration_applied: boolean;
      fields_protected: number;
      fields_restored: number;
    };
    processing_summary: {
      processing_time_ms: number;
      status: string;
    };
    analysis: {
      obfuscated_response: string;
      restored_response: string;
      tokens_used: number;
    };
    security_guarantee: string;
    privacy_note: string;
  }>;
}

const PIIProtectionDemos: React.FC<PIIDemoProps> = ({ apiBaseUrl = '' }) => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [demo1Loading, setDemo1Loading] = useState(false);
  const [demo2Loading, setDemo2Loading] = useState(false);
  const [demo1Results, setDemo1Results] = useState<TicketDemo | null>(null);
  const [demo2Results, setDemo2Results] = useState<FinancialDemo | null>(null);
  const [demo1Error, setDemo1Error] = useState<string | null>(null);
  const [demo2Error, setDemo2Error] = useState<string | null>(null);
  const [demo1Expanded, setDemo1Expanded] = useState(false);
  const [demo2Expanded, setDemo2Expanded] = useState(false);
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleModalType, setSampleModalType] = useState<'demo1' | 'demo2'>('demo1');

  const runDemo1 = async () => {
    setDemo1Loading(true);
    setDemo1Error(null);
    setDemo1Results(null);

    try {
      const response = await axios.post(`${apiBaseUrl}/api/demos/ticket-support`, {});
      setDemo1Results(response.data);
      setDemo1Expanded(true);
    } catch (error: any) {
      setDemo1Error(error.response?.data?.message || 'Failed to run demo. Please try again.');
      console.error('Demo 1 error:', error);
    } finally {
      setDemo1Loading(false);
    }
  };

  const runDemo2 = async () => {
    setDemo2Loading(true);
    setDemo2Error(null);
    setDemo2Results(null);

    try {
      const response = await axios.post(`${apiBaseUrl}/api/demos/financial-application`, {});
      setDemo2Results(response.data);
      setDemo2Expanded(true);
    } catch (error: any) {
      setDemo2Error(error.response?.data?.message || 'Failed to run demo. Please try again.');
      console.error('Demo 2 error:', error);
    } finally {
      setDemo2Loading(false);
    }
  };

  const showSampleData = (type: 'demo1' | 'demo2') => {
    setSampleModalType(type);
    setSampleModalOpen(true);
  };

  const closeSampleModal = () => {
    setSampleModalOpen(false);
  };

  const sampleTickets = [
    {
      ticket_id: 'TKT-2024-001',
      customer_name: 'Sarah Johnson',
      email: 'sarah.johnson@acmecorp.com',
      phone: '555-123-4567',
      priority: 'high',
      description: 'Cannot access company VPN. Getting error "Connection timeout"...'
    },
    {
      ticket_id: 'TKT-2024-002',
      customer_name: 'Michael Chen',
      email: 'mchen@techstartup.io',
      phone: '+1-555-987-6543',
      priority: 'medium',
      description: 'Email not syncing on mobile device...'
    }
  ];

  const sampleApplications = [
    {
      application_id: 'APP-2024-8756',
      submission_date: '2024-01-15T10:30:00Z',
      application_type: 'home_purchase',
      applicant: {
        name: 'Robert James Thompson',
        ssn: '123-45-6789',
        dob: '1985-03-15',
        email: 'robert.thompson@email.com',
        phone: '555-123-4567',
        drivers_license: 'IL D123-4567-8901',
        current_address: '742 Evergreen Terrace, Springfield, IL 62701',
        previous_address: '123 Main Street, Chicago, IL 60601',
        years_at_current: 3,
        housing_status: 'Renting ($1,400/month)'
      },
      employment: {
        current: {
          employer: 'Acme Corporation',
          position: 'Senior Software Engineer',
          annual_income: 125000,
          years: 5.3,
          supervisor: 'Michael Chen',
          work_email: 'rthompson@acmecorp.com'
        },
        previous: {
          employer: 'TechStart Industries',
          income: 75000
        },
        additional_income: [
          { source: 'Freelance consulting', amount: 15000 },
          { source: 'Rental property', amount: 18000 }
        ]
      },
      financial: {
        credit_score: 742,
        monthly_gross_income: 10417,
        debt_to_income_ratio: 0.16,
        assets: {
          checking_savings: 75700,
          retirement_total: 227000,
          investments: 78000,
          real_estate_equity: 60000,
          vehicles_equity: 13000,
          total: 453700
        },
        liabilities: {
          credit_cards: 7000,
          student_loans: 18000,
          auto_loan: 15000,
          investment_mortgage: 120000,
          monthly_debt: 1700,
          total: 160000
        },
        net_worth: 293700
      },
      loan: {
        amount_requested: 320000,
        down_payment: 80000,
        term_years: 30,
        purpose: 'Primary residence purchase',
        property_address: '456 Dream Home Drive, Springfield, IL 62702',
        property_value: 400000,
        estimated_payment: 1920
      },
      references: [
        { name: 'Sarah Williams', relationship: 'Colleague', phone: '555-200-3000' },
        { name: 'David Anderson', relationship: 'Friend', phone: '555-300-4000' }
      ]
    },
    {
      application_id: 'APP-2024-8757',
      submission_date: '2024-01-16T14:15:00Z',
      application_type: 'debt_consolidation',
      applicant: {
        name: 'Jennifer Marie Martinez',
        ssn: '987-65-4321',
        dob: '1990-07-22',
        email: 'jennifer.martinez@email.com',
        phone: '555-987-6543',
        drivers_license: 'OR D987-6543-2109',
        current_address: '456 Oak Street, Apt 3B, Portland, OR 97201',
        previous_address: '789 Cedar Avenue, Eugene, OR 97401',
        years_at_current: 2,
        housing_status: 'Renting ($1,650/month)'
      },
      employment: {
        current: {
          employer: 'Portland Medical Center',
          position: 'Registered Nurse - ICU',
          annual_income: 88000,
          years: 3.8,
          supervisor: 'Dr. Patricia Wong',
          work_email: 'jmartinez@portlandmed.org'
        },
        previous: {
          employer: 'Eugene General Hospital',
          income: 68000
        },
        additional_income: [{ source: 'Per-diem nursing shifts', amount: 12000 }]
      },
      financial: {
        credit_score: 668,
        monthly_gross_income: 7333,
        debt_to_income_ratio: 0.23,
        assets: {
          checking_savings: 23800,
          retirement_total: 42000,
          investments: 13000,
          vehicles_equity: 10000,
          total: 88800
        },
        liabilities: {
          credit_cards: 19500,
          student_loans: 67000,
          auto_loan: 8000,
          medical_debt: 3200,
          monthly_debt: 1670,
          total: 97700
        },
        net_worth: -8900
      },
      loan: {
        amount_requested: 48000,
        down_payment: 0,
        term_years: 5,
        purpose: 'Consolidate high-interest credit card debt and private student loans',
        estimated_payment: 920,
        will_payoff: 'Credit cards ($19.5K) + Private student loan ($22K)'
      },
      references: [
        { name: 'Emily Rodriguez', relationship: 'Colleague', phone: '555-500-6000' },
        { name: 'Thomas Lee', relationship: 'Former Supervisor', phone: '555-600-7000' }
      ]
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Enhanced Header Section */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: 'primary.main',
              border: `3px solid ${theme.palette.primary.main}`
            }}
          >
            <ShieldIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.75rem', sm: '2.5rem' },
                mb: 1
              }}
            >
              GuardLLM PII Protection Demos
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              Experience real-time PII protection with two powerful approaches: permanent cleansing
              and reversible obfuscation
            </Typography>
          </Box>
        </Stack>

        {/* Key Features Banner */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 3 }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          {[
            { icon: <SecurityIcon />, label: 'Zero PII Exposure', color: 'success' as const },
            { icon: <AutoAwesomeIcon />, label: 'AI-Powered Analysis', color: 'info' as const },
            { icon: <SpeedIcon />, label: 'Real-time Processing', color: 'warning' as const }
          ].map((feature, index) => (
            <Zoom in timeout={300 + index * 100} key={index}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette[feature.color].main, 0.15),
                    color: `${feature.color}.main`
                  }}
                >
                  {feature.icon}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {feature.label}
                </Typography>
              </Stack>
            </Zoom>
          ))}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Demo 1: Ticket Support - Enhanced */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={500}>
            <Card
              elevation={3}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 32px ${alpha(theme.palette.info.main, 0.2)}`,
                  borderColor: theme.palette.info.main
                }
              }}
            >
              <Box
                sx={{
                  p: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`,
                  borderBottom: `2px solid ${alpha(theme.palette.info.main, 0.3)}`
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: alpha(theme.palette.info.main, 0.15),
                      color: 'info.main',
                      border: `2px solid ${theme.palette.info.main}`
                    }}
                  >
                    <SupportAgentIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Demo 1: IT Support Tickets
                    </Typography>
                    <Chip
                      label="PII Cleansing (Permanent)"
                      size="small"
                      color="info"
                      icon={<LockIcon />}
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Alert
                  severity="info"
                  icon={<InfoIcon />}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Use Case: Pattern Analysis & Knowledge Base
                  </Typography>
                  <Typography variant="body2">
                    Analyze support tickets to identify issue patterns and resolutions without
                    exposing customer PII.
                  </Typography>
                </Alert>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.1)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}
                  >
                    <VerifiedUserIcon sx={{ fontSize: 18, mr: 1, color: 'info.main' }} />
                    Key Features
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      'Detects and removes all PII permanently',
                      'Generates issue categorization',
                      'Ideal for analytics and knowledge bases',
                      'No PII restoration needed'
                    ].map((feature, index) => (
                      <Stack key={index} direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'info.main' }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, display: 'block', mb: 1 }}
                  >
                    SAMPLE DATA
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    5 support tickets with customer names, emails, phone numbers, and IP addresses
                  </Typography>
                </Paper>

                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<DataObjectIcon />}
                    onClick={() => showSampleData('demo1')}
                    fullWidth
                    sx={{
                      borderWidth: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.2)}`
                      }
                    }}
                  >
                    View Sample Data
                  </Button>

                  <Button
                    variant="contained"
                    color="info"
                    fullWidth
                    size="large"
                    startIcon={
                      demo1Loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <PlayArrowIcon />
                      )
                    }
                    disabled={demo1Loading}
                    onClick={runDemo1}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.3)}`,
                      '&:hover': {
                        boxShadow: `0 6px 16px ${alpha(theme.palette.info.main, 0.4)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {demo1Loading ? 'Processing Tickets...' : 'Run Ticket Support Demo'}
                  </Button>
                </Stack>

                {demo1Loading && (
                  <Fade in>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.info.main
                          }
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
                      >
                        Analyzing tickets and cleansing PII...
                      </Typography>
                    </Box>
                  </Fade>
                )}

                {demo1Error && (
                  <Fade in>
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {demo1Error}
                    </Alert>
                  </Fade>
                )}

                {demo1Results && (
                  <>
                    <Divider sx={{ my: 2 }} />

                    {/* Summary Stats */}
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter', mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <AssessmentIcon sx={{ color: 'success.main' }} />
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: 600, mb: 0 }}
                        >
                          Processing Summary
                        </Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Tickets Processed
                          </Typography>
                          <Typography variant="h6">
                            {demo1Results.processing_summary.tickets_processed}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            PII Items Cleansed
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {demo1Results.processing_summary.pii_items_cleansed}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Processing Time
                          </Typography>
                          <Typography variant="body1">
                            {demo1Results.processing_summary?.processing_time_ms || 0}ms
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={demo1Results.processing_summary.status}
                            color="success"
                            size="small"
                            icon={<CheckCircleIcon />}
                          />
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* PII Details */}
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <LockIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          PII Cleansing Details - Data Transformation
                        </Typography>
                      </Stack>
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>Ticket ID</strong>
                              </TableCell>
                              <TableCell>
                                <strong>PII Items Removed</strong>
                              </TableCell>
                              <TableCell>
                                <strong>PII Types</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Result</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {demo1Results.cleansing_details.map((detail, index) => (
                              <TableRow key={index}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {detail.ticket_id}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${detail.pii_removed} items`}
                                    size="small"
                                    color="warning"
                                  />
                                </TableCell>
                                <TableCell>
                                  {detail.pii_types.map((type) => (
                                    <Chip
                                      key={type}
                                      label={type}
                                      size="small"
                                      sx={{ ml: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label="Cleansed"
                                    size="small"
                                    color="success"
                                    icon={<CheckCircleIcon />}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.lighter' }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Example Transformation:</strong> "Contact Sarah Johnson
                          (sarah@email.com, 555-1234)" → "Contact [NAME] ([EMAIL], [PHONE])"
                        </Typography>
                      </Paper>
                    </Box>

                    {/* Analysis */}
                    <Box>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <SmartToyIcon sx={{ color: 'primary.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            LLM Analysis (Based on Cleansed Data)
                          </Typography>
                        </Stack>
                        <IconButton size="small" onClick={() => setDemo1Expanded(!demo1Expanded)}>
                          {demo1Expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Stack>
                      <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Note:</strong> The LLM received tickets with all PII removed and
                          never saw the original customer data.
                        </Typography>
                      </Alert>
                      <Collapse in={demo1Expanded}>
                        <Paper
                          sx={{
                            p: 3,
                            mt: 1,
                            bgcolor: 'grey.50',
                            maxHeight: 500,
                            overflow: 'auto',
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            '& h3': {
                              color: 'primary.main',
                              mt: 2,
                              mb: 1
                            },
                            '& ul': {
                              pl: 4
                            },
                            '& li': {
                              my: 1
                            }
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatMarkdownToHTML(demo1Results.analysis.llm_response)
                          }}
                        />
                      </Collapse>
                    </Box>

                    {/* Privacy Guarantee */}
                    <Alert severity="success" icon={<LockIcon />} sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {demo1Results.privacy_guarantee}
                      </Typography>
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        {demo1Results.note}
                      </Typography>
                    </Alert>
                  </>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Demo 2: Financial Applications */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <AttachMoneyIcon sx={{ fontSize: 32, color: 'success.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" component="h2">
                    Demo 2: Financial Applications
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    PII Obfuscation with Restoration
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="body2" paragraph>
                <strong>Use Case:</strong> Process loan applications with LLM risk assessment while
                protecting sensitive data, then provide personalized responses.
              </Typography>

              <Paper sx={{ p: 2, bgcolor: 'success.lighter', mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Features:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    ✓ Obfuscates PII with temporary identifiers
                  </Typography>
                  <Typography variant="body2">✓ LLM never sees actual sensitive data</Typography>
                  <Typography variant="body2">✓ Restores PII for personalized responses</Typography>
                  <Typography variant="body2">
                    ✓ Perfect for financial/healthcare use cases
                  </Typography>
                </Stack>
              </Paper>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Sample Data:</strong> 2 loan applications with names, SSNs, DOBs, financial
                details.
              </Typography>

              <Button
                variant="outlined"
                color="success"
                startIcon={<DataObjectIcon />}
                onClick={() => showSampleData('demo2')}
                sx={{ mb: 2, mr: 2 }}
              >
                View Sample Data
              </Button>

              <Button
                variant="contained"
                color="success"
                fullWidth
                startIcon={
                  demo2Loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />
                }
                disabled={demo2Loading}
                onClick={runDemo2}
                sx={{ mb: 2 }}
              >
                {demo2Loading ? 'Processing Applications...' : 'Run Financial Application Demo'}
              </Button>

              {demo2Error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {demo2Error}
                </Alert>
              )}

              {demo2Results && demo2Results.results.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {demo2Results.results.map((result, index) => (
                    <Box key={index}>
                      {/* Summary Stats */}
                      <Paper sx={{ p: 2, bgcolor: 'info.lighter', mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <AssessmentIcon sx={{ color: 'info.main' }} />
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 600, mb: 0 }}
                          >
                            Processing Summary
                          </Typography>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Application ID
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                              {result.application_id}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Processing Time
                            </Typography>
                            <Typography variant="body1">
                              {result.processing_summary?.processing_time_ms || 0}ms
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Fields Obfuscated
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              {result.pii_protection?.fields_protected || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Fields Restored
                            </Typography>
                            <Typography variant="h6" color="success.main">
                              {result.pii_protection?.fields_restored || 0}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Data Transformation - Dynamic based on actual data */}
                      <Box sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <TransformIcon sx={{ color: 'warning.main' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            PII Protection Applied
                          </Typography>
                        </Stack>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>How it works:</strong> Sensitive data like names, SSNs, emails,
                            and addresses were replaced with temporary identifiers (e.g.,
                            APPLICANT_1, XXX-XX-0001) before sending to the LLM. After processing,
                            the original data was restored in the response.
                          </Typography>
                        </Alert>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Example Transformations:
                              </Typography>
                              <Stack spacing={1} sx={{ mt: 1 }}>
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="warning.main"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Original → Obfuscated
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                                  >
                                    Robert Thompson → APPLICANT_1
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                                  >
                                    123-45-6789 → XXX-XX-0001
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                                  >
                                    robert@email.com → applicant1@example.com
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Protection Summary:
                              </Typography>
                              <Stack spacing={1} sx={{ mt: 1 }}>
                                <Chip
                                  label={`${result.pii_protection?.fields_protected || 0} fields protected`}
                                  size="small"
                                  color="warning"
                                  icon={<LockIcon />}
                                />
                                <Chip
                                  label={`${result.pii_protection?.fields_restored || 0} fields restored`}
                                  size="small"
                                  color="success"
                                  icon={<RestoreIcon />}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  LLM never saw actual PII
                                </Typography>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>

                      {/* Response Comparison */}
                      <Box sx={{ mb: 2 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => setDemo2Expanded(!demo2Expanded)}
                          endIcon={demo2Expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          sx={{ mb: 2 }}
                        >
                          {demo2Expanded ? 'Hide' : 'Show'} Response Comparison (Obfuscated vs
                          Restored)
                        </Button>

                        <Collapse in={demo2Expanded} timeout="auto">
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <strong>Compare:</strong> See how the LLM response with obfuscated
                              data differs from the final user-facing response with restored PII.
                            </Typography>
                          </Alert>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  bgcolor: 'warning.lighter',
                                  border: '2px solid',
                                  borderColor: 'warning.main',
                                  borderRadius: 2
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, color: 'warning.dark' }}
                                  >
                                    What LLM Saw (Obfuscated)
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    bgcolor: 'background.paper',
                                    p: 2,
                                    borderRadius: 1,
                                    '& h3': {
                                      color: 'warning.main',
                                      mt: 1,
                                      mb: 1,
                                      fontSize: '1em'
                                    },
                                    '& ul': {
                                      pl: 2,
                                      my: 1
                                    },
                                    '& li': {
                                      my: 0.5,
                                      fontSize: '0.9em'
                                    },
                                    '& p': {
                                      my: 1,
                                      fontSize: '0.9em'
                                    }
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatMarkdownToHTML(
                                      result.analysis?.obfuscated_response ||
                                        'No obfuscated response available'
                                    )
                                  }}
                                />
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  bgcolor: 'success.lighter',
                                  border: '2px solid',
                                  borderColor: 'success.main',
                                  borderRadius: 2
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: 2 }}
                                >
                                  <RestoreIcon sx={{ fontSize: 20, color: 'success.main' }} />
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700, color: 'success.dark' }}
                                  >
                                    What User Sees (Restored)
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    bgcolor: 'background.paper',
                                    p: 2,
                                    borderRadius: 1,
                                    '& h3': {
                                      color: 'success.main',
                                      mt: 1,
                                      mb: 1,
                                      fontSize: '1em'
                                    },
                                    '& ul': {
                                      pl: 2,
                                      my: 1
                                    },
                                    '& li': {
                                      my: 0.5,
                                      fontSize: '0.9em'
                                    },
                                    '& p': {
                                      my: 1,
                                      fontSize: '0.9em'
                                    }
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatMarkdownToHTML(
                                      result.analysis?.restored_response ||
                                        'No restored response available'
                                    )
                                  }}
                                />
                              </Paper>
                            </Grid>
                          </Grid>
                        </Collapse>
                      </Box>

                      {/* Security Guarantee */}
                      <Alert severity="success" icon={<LockIcon />} sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {result.security_guarantee || 'PII protection applied successfully'}
                        </Typography>
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                          {result.privacy_note || 'Data was processed securely'}
                        </Typography>
                      </Alert>
                    </Box>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Info Section */}
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, bgcolor: 'primary.lighter' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <HelpOutlineIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Which Demo Should You Use?</Typography>
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <CheckBoxIcon sx={{ color: 'info.main' }} />
                <Typography variant="subtitle2" color="info.main">
                  Use Demo 1 (Cleansing) When:
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">✓ You need to analyze patterns/trends</Typography>
                <Typography variant="body2">✓ You DON'T need personalized responses</Typography>
                <Typography variant="body2">✓ You're building reports or dashboards</Typography>
                <Typography variant="body2">✓ PII should never be recoverable</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <CheckBoxIcon sx={{ color: 'success.main' }} />
                <Typography variant="subtitle2" color="success.main">
                  Use Demo 2 (Obfuscation) When:
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="body2">✓ You need personalized responses</Typography>
                <Typography variant="body2">✓ Users expect to see their own data</Typography>
                <Typography variant="body2">✓ You want conversational context</Typography>
                <Typography variant="body2">✓ Compliance requires data minimization</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Sample Data Modal */}
      <Dialog open={sampleModalOpen} onClose={closeSampleModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <DataObjectIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6">
                {sampleModalType === 'demo1' ? 'Sample Ticket Data' : 'Sample Application Data'}
              </Typography>
            </Stack>
            <IconButton onClick={closeSampleModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong>{' '}
              {sampleModalType === 'demo1'
                ? 'This data contains PII (names, emails, phones, IPs) that will be cleansed before sending to the LLM.'
                : 'This data contains sensitive PII (SSN, DOB, financial info) that will be obfuscated before sending to the LLM, then restored in the response.'}
            </Typography>
          </Alert>

          {sampleModalType === 'demo1' ? (
            <>
              {sampleTickets.map((ticket, idx) => (
                <Paper key={idx} sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Ticket {idx + 1}: {ticket.ticket_id}
                  </Typography>
                  <Box sx={{ lineHeight: 1.8 }}>
                    <Typography variant="body1" paragraph>
                      <strong>Customer:</strong> {ticket.customer_name} ({ticket.email},{' '}
                      {ticket.phone})
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Priority:</strong> {ticket.priority.toUpperCase()} |{' '}
                      <strong>Status:</strong> Open
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Issue Description:</strong> {ticket.description}
                    </Typography>
                  </Box>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      <strong>PII Detected:</strong> Customer name, email address, phone number
                    </Typography>
                  </Alert>
                </Paper>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Total: {sampleTickets.length} tickets shown (5 tickets will be processed in the
                demo)
              </Typography>
            </>
          ) : (
            <>
              {sampleApplications.map((app, idx) => (
                <Paper key={idx} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    Application {idx + 1}: {app.application_id}
                  </Typography>
                  <Box sx={{ lineHeight: 1.8 }}>
                    <Typography variant="body1" paragraph>
                      <strong>Application Type:</strong> {app.application_type}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Applicant:</strong> {app.applicant.name}, born {app.applicant.dob}{' '}
                      (SSN: {app.applicant.ssn}, DL: {app.applicant.drivers_license}). Contact:{' '}
                      {app.applicant.email}, {app.applicant.phone}.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Current Address:</strong> {app.applicant.current_address}, living
                      there for {app.applicant.years_at_current} years (
                      {app.applicant.housing_status}).
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Employment:</strong> Works as {app.employment.current.position} at{' '}
                      {app.employment.current.employer}, earning $
                      {app.employment.current.annual_income.toLocaleString()}/year for{' '}
                      {app.employment.current.years} years. Supervisor:{' '}
                      {app.employment.current.supervisor}. Work email:{' '}
                      {app.employment.current.work_email}.
                    </Typography>
                    {app.employment.additional_income.length > 0 && (
                      <Typography variant="body1" paragraph>
                        <strong>Additional Income:</strong>{' '}
                        {app.employment.additional_income
                          .map((inc) => `${inc.source} ($${inc.amount.toLocaleString()})`)
                          .join(', ')}
                        .
                      </Typography>
                    )}
                    <Typography variant="body1" paragraph>
                      <strong>Financial Summary:</strong> Credit score {app.financial.credit_score},
                      monthly gross income ${app.financial.monthly_gross_income.toLocaleString()},
                      debt-to-income ratio {app.financial.debt_to_income_ratio}, net worth $
                      {app.financial.net_worth >= 0 ? '+' : ''}$
                      {app.financial.net_worth.toLocaleString()}.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Assets:</strong> Total ${app.financial.assets.total.toLocaleString()}{' '}
                      including checking/savings ($
                      {app.financial.assets.checking_savings.toLocaleString()}), retirement accounts
                      (${app.financial.assets.retirement_total.toLocaleString()}), investments ($
                      {app.financial.assets.investments.toLocaleString()}), and vehicles ($
                      {app.financial.assets.vehicles_equity.toLocaleString()}).
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Liabilities:</strong> Total $
                      {app.financial.liabilities.total.toLocaleString()} including credit cards ($
                      {app.financial.liabilities.credit_cards.toLocaleString()}), student loans ($
                      {app.financial.liabilities.student_loans.toLocaleString()}), auto loan ($
                      {app.financial.liabilities.auto_loan.toLocaleString()}). Monthly debt
                      payments: ${app.financial.liabilities.monthly_debt.toLocaleString()}.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Loan Request:</strong> Requesting $
                      {app.loan.amount_requested.toLocaleString()}{' '}
                      {app.loan.down_payment > 0
                        ? `with ${app.loan.down_payment.toLocaleString()} down payment`
                        : '(no down payment)'}{' '}
                      over {app.loan.term_years} years. Purpose: {app.loan.purpose}. Estimated
                      monthly payment: ${app.loan.estimated_payment.toLocaleString()}.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>References:</strong>{' '}
                      {app.references
                        .map((ref) => `${ref.name} (${ref.relationship}, ${ref.phone})`)
                        .join(', ')}
                      .
                    </Typography>
                  </Box>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <LockIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      <strong>PII Detected:</strong> Name, SSN, DOB, Driver's License, Email, Phone,
                      Address, Employment details, Financial account information, Reference contacts
                    </Typography>
                  </Alert>
                </Paper>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Total: {sampleApplications.length} applications will be processed
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSampleModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PIIProtectionDemos;
